import { useState } from 'react';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { Palette, Check, RotateCcw, Pipette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useTemplateDesigner } from '../TemplateDesignerContext';
import type { ColorTheme } from '../types';

const PRESET_THEMES: { name: string; colors: ColorTheme }[] = [
  {
    name: 'Indigo Pro',
    colors: {
      primary: '#6366f1',
      secondary: '#8b5cf6',
      accent: '#06b6d4',
      headerBg: '#f8fafc',
      headerText: '#0f172a',
      tableBorder: '#e2e8f0',
      tableHeaderBg: '#f1f5f9',
      tableHeaderText: '#334155',
      tableAltRow: '#fafafa',
      divider: '#e2e8f0',
      total: '#0f172a',
    },
  },
  {
    name: 'Ocean Blue',
    colors: {
      primary: '#0ea5e9',
      secondary: '#38bdf8',
      accent: '#14b8a6',
      headerBg: '#f0f9ff',
      headerText: '#0c4a6e',
      tableBorder: '#bae6fd',
      tableHeaderBg: '#e0f2fe',
      tableHeaderText: '#0369a1',
      tableAltRow: '#f8fafc',
      divider: '#bae6fd',
      total: '#0c4a6e',
    },
  },
  {
    name: 'Forest Green',
    colors: {
      primary: '#10b981',
      secondary: '#34d399',
      accent: '#06b6d4',
      headerBg: '#f0fdf4',
      headerText: '#14532d',
      tableBorder: '#bbf7d0',
      tableHeaderBg: '#dcfce7',
      tableHeaderText: '#166534',
      tableAltRow: '#f8fafc',
      divider: '#bbf7d0',
      total: '#14532d',
    },
  },
  {
    name: 'Sunset Orange',
    colors: {
      primary: '#f97316',
      secondary: '#fb923c',
      accent: '#facc15',
      headerBg: '#fff7ed',
      headerText: '#7c2d12',
      tableBorder: '#fed7aa',
      tableHeaderBg: '#ffedd5',
      tableHeaderText: '#c2410c',
      tableAltRow: '#fffbeb',
      divider: '#fed7aa',
      total: '#7c2d12',
    },
  },
  {
    name: 'Royal Purple',
    colors: {
      primary: '#9333ea',
      secondary: '#a855f7',
      accent: '#ec4899',
      headerBg: '#faf5ff',
      headerText: '#581c87',
      tableBorder: '#e9d5ff',
      tableHeaderBg: '#f3e8ff',
      tableHeaderText: '#7e22ce',
      tableAltRow: '#fdf4ff',
      divider: '#e9d5ff',
      total: '#581c87',
    },
  },
  {
    name: 'Slate Modern',
    colors: {
      primary: '#475569',
      secondary: '#64748b',
      accent: '#0ea5e9',
      headerBg: '#f8fafc',
      headerText: '#0f172a',
      tableBorder: '#e2e8f0',
      tableHeaderBg: '#f1f5f9',
      tableHeaderText: '#334155',
      tableAltRow: '#fafafa',
      divider: '#e2e8f0',
      total: '#0f172a',
    },
  },
  {
    name: 'Ruby Red',
    colors: {
      primary: '#dc2626',
      secondary: '#ef4444',
      accent: '#f97316',
      headerBg: '#fef2f2',
      headerText: '#7f1d1d',
      tableBorder: '#fecaca',
      tableHeaderBg: '#fee2e2',
      tableHeaderText: '#b91c1c',
      tableAltRow: '#fff7f7',
      divider: '#fecaca',
      total: '#7f1d1d',
    },
  },
  {
    name: 'Midnight',
    colors: {
      primary: '#1e293b',
      secondary: '#334155',
      accent: '#6366f1',
      headerBg: '#f1f5f9',
      headerText: '#0f172a',
      tableBorder: '#cbd5e1',
      tableHeaderBg: '#1e293b',
      tableHeaderText: '#f8fafc',
      tableAltRow: '#f8fafc',
      divider: '#cbd5e1',
      total: '#0f172a',
    },
  },
];

const COLOR_FIELDS: { key: keyof ColorTheme; label: string; group: string }[] = [
  { key: 'primary', label: 'Primary', group: 'Brand' },
  { key: 'secondary', label: 'Secondary', group: 'Brand' },
  { key: 'accent', label: 'Accent', group: 'Brand' },
  { key: 'headerBg', label: 'Header Background', group: 'Header' },
  { key: 'headerText', label: 'Header Text', group: 'Header' },
  { key: 'tableHeaderBg', label: 'Table Header Bg', group: 'Table' },
  { key: 'tableHeaderText', label: 'Table Header Text', group: 'Table' },
  { key: 'tableBorder', label: 'Table Border', group: 'Table' },
  { key: 'tableAltRow', label: 'Alternate Row', group: 'Table' },
  { key: 'divider', label: 'Dividers', group: 'Other' },
  { key: 'total', label: 'Total Text', group: 'Other' },
];

export function ColorPanel() {
  const { settings, updateSettings } = useTemplateDesigner();
  const { colorTheme } = settings;
  const [activeGroup, setActiveGroup] = useState('Brand');

  const updateColor = (key: keyof ColorTheme, value: string) => {
    updateSettings('colorTheme', { ...colorTheme, [key]: value });
  };

  const applyPreset = (preset: typeof PRESET_THEMES[0]) => {
    updateSettings('colorTheme', preset.colors);
  };

  const groups = [...new Set(COLOR_FIELDS.map(f => f.group))];

  return (
    <div className="space-y-6">
      {/* Preset Themes */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold flex items-center gap-2">
          <Palette className="w-4 h-4 text-primary" />
          Color Presets
        </Label>

        <div className="grid grid-cols-4 gap-2">
          {PRESET_THEMES.map((preset) => (
            <button
              key={preset.name}
              onClick={() => applyPreset(preset)}
              className="group relative aspect-square rounded-xl overflow-hidden border-2 border-border hover:border-primary/50 transition-all shadow-sm hover:shadow-md"
              title={preset.name}
            >
              {/* Color Preview */}
              <div className="absolute inset-0 grid grid-cols-2 grid-rows-2">
                <div style={{ backgroundColor: preset.colors.primary }} />
                <div style={{ backgroundColor: preset.colors.secondary }} />
                <div style={{ backgroundColor: preset.colors.accent }} />
                <div style={{ backgroundColor: preset.colors.headerBg }} />
              </div>

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Check className="w-5 h-5 text-white" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Colors */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold flex items-center gap-2">
            <Pipette className="w-4 h-4 text-primary" />
            Custom Colors
          </Label>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => applyPreset(PRESET_THEMES[0])}
          >
            <RotateCcw className="w-3 h-3 mr-1" />
            Reset
          </Button>
        </div>

        {/* Group Tabs */}
        <div className="flex gap-1 p-1 bg-muted rounded-lg">
          {groups.map((group) => (
            <button
              key={group}
              onClick={() => setActiveGroup(group)}
              className={`
                flex-1 py-1.5 px-2 rounded-md text-xs font-medium transition-all
                ${activeGroup === group
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
                }
              `}
            >
              {group}
            </button>
          ))}
        </div>

        {/* Color Pickers */}
        <div className="space-y-2">
          {COLOR_FIELDS.filter(f => f.group === activeGroup).map((field) => (
            <ColorPickerRow
              key={field.key}
              label={field.label}
              value={colorTheme[field.key]}
              onChange={(value) => updateColor(field.key, value)}
            />
          ))}
        </div>
      </div>

      {/* Live Preview Strip */}
      <div className="space-y-2">
        <Label className="text-xs text-muted-foreground">Preview</Label>
        <div className="h-16 rounded-lg overflow-hidden border border-border shadow-sm">
          <div
            className="h-4"
            style={{ background: `linear-gradient(to right, ${colorTheme.primary}, ${colorTheme.secondary})` }}
          />
          <div
            className="h-12 p-2 flex items-center justify-between"
            style={{ backgroundColor: colorTheme.headerBg }}
          >
            <div className="flex items-center gap-2">
              <div
                className="w-6 h-6 rounded"
                style={{ backgroundColor: colorTheme.primary }}
              />
              <div className="space-y-1">
                <div
                  className="h-2 w-16 rounded"
                  style={{ backgroundColor: colorTheme.headerText, opacity: 0.8 }}
                />
                <div
                  className="h-1.5 w-10 rounded"
                  style={{ backgroundColor: colorTheme.headerText, opacity: 0.4 }}
                />
              </div>
            </div>
            <div
              className="px-2 py-1 rounded text-xs font-bold"
              style={{ backgroundColor: colorTheme.accent, color: '#fff' }}
            >
              $1,234
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ColorPickerRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
      <span className="text-sm font-medium">{label}</span>
      <Popover>
        <PopoverTrigger asChild>
          <button className="flex items-center gap-2 group">
            <div
              className="w-8 h-8 rounded-lg border-2 border-border shadow-sm transition-all group-hover:scale-105 group-hover:shadow-md"
              style={{ backgroundColor: value }}
            />
            <span className="text-xs font-mono text-muted-foreground uppercase">
              {value}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="end">
          <div className="space-y-3">
            <HexColorPicker color={value} onChange={onChange} />
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">#</span>
              <HexColorInput
                color={value}
                onChange={onChange}
                className="flex-1 px-2 py-1 text-sm font-mono uppercase bg-muted rounded border border-border focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
